import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { mockData } from '../data/mock';

const AboutPage = () => {
  const [aboutInfo, setAboutInfo] = useState({});

  useEffect(() => {
    setAboutInfo(mockData.aboutInfo);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/b1q8t6pz_Picture%201.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/90 to-teal-900/80"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg">
              About Fenix Partnership
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
              We are owners and developers, not operators. Our focus is on creating exceptional 
              memorial garden facilities from conception to completion, then partnering with 
              expert management teams for ongoing operations.
            </p>
            <Badge variant="outline" className="text-emerald-300 border-emerald-300 px-4 py-2 bg-white/10 backdrop-blur-sm">
              {aboutInfo.experience}
            </Badge>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Vision</h2>
              <p className="text-lg text-gray-700 leading-relaxed text-xl">
                {aboutInfo.vision}
              </p>
            </div>
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/8y5oakel_Image%2059.jpeg" 
                alt="Memorial Building Architecture" 
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-xl font-semibold text-white mb-2 drop-shadow-lg">Beautiful Spaces</h3>
                <p className="text-white/90 text-sm">
                  Places for the living to remember, reflect, and find comfort in beautifully designed environments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/mucgez4r_Image%2061.jpeg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/85 to-violet-900/75"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/qxpttghu_Picture%203.jpg" 
                alt="Contemporary Chapel Design" 
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-xl font-semibold text-white mb-2 drop-shadow-lg">Development Focus</h3>
                <p className="text-white/90 text-sm">
                  We concentrate on what we do best - developing beautiful, innovative memorial spaces.
                </p>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-6 drop-shadow-lg">Our Approach</h2>
              <p className="text-lg text-white/90 leading-relaxed text-xl">
                {aboutInfo.approach}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Areas of Expertise</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              With over 15 years of experience, we bring specialized knowledge to every 
              aspect of memorial garden development.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aboutInfo.expertise && aboutInfo.expertise.map((area, index) => (
              <Card key={index} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-white text-lg">{area}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Sets Us Apart</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're not traditional cemetery operators. We're innovators focused on creating 
              beautiful spaces that transform how people think about memorial gardens.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white transform hover:scale-105 transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-4">Development, Not Operations</h3>
                <p className="text-amber-100 mb-4">
                  We focus exclusively on creating exceptional facilities, then partner with 
                  experienced management teams for day-to-day operations.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-200 rounded-full"></div>
                    <span className="text-sm text-amber-100">Specialized development expertise</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-200 rounded-full"></div>
                    <span className="text-sm text-amber-100">Partnership-based operations</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-200 rounded-full"></div>
                    <span className="text-sm text-amber-100">Quality oversight and standards</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-gradient-to-br from-purple-500 to-violet-600 text-white transform hover:scale-105 transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-4">Innovation in Design</h3>
                <p className="text-purple-100 mb-4">
                  We move beyond traditional cemetery concepts to create memorial gardens 
                  as beautiful parks for the living.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-purple-200 rounded-full"></div>
                    <span className="text-sm text-purple-100">Seasonal themed landscapes</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-purple-200 rounded-full"></div>
                    <span className="text-sm text-purple-100">Contemporary architectural elements</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-purple-200 rounded-full"></div>
                    <span className="text-sm text-purple-100">Environmental sustainability focus</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-16 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Professional Credentials</h2>
            <p className="text-emerald-100">
              All our developments meet the highest industry standards and regulatory requirements.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="text-sm font-medium text-white">ICCM Standards</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-white">FBCA Inspections</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-white">Ministry Compliance</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-white">Green Flag Standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/lvrtfpku_Image%2058.jpeg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-amber-900/90 to-orange-900/80"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">Partner With Us</h2>
          <p className="text-xl mb-8 text-white/90">
            Whether you're interested in development opportunities, partnership possibilities, 
            or learning more about our approach, we'd love to discuss the possibilities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 shadow-xl">
                Get In Touch
              </Button>
            </Link>
            <Link to="/masterplan">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/20 backdrop-blur-sm px-8 py-3">
                See The Masterplan
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;